import axios from 'axios';
import { API_BASE_URL } from './config';

// Interfaces
export interface Notification {
  _id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'reminder';
  category: 'system' | 'project' | 'safety' | 'quality' | 'schedule' | 'budget' | 'user';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  recipient: {
    _id: string;
    name: string;
    email: string;
    role: string;
  };
  sender?: {
    _id: string;
    name: string;
    email: string;
  };
  relatedEntity?: {
    type: 'project' | 'issue' | 'document' | 'user' | 'equipment';
    id: string;
    name: string;
  };
  channels: NotificationChannel[];
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  isRead: boolean;
  readAt?: string;
  scheduledFor?: string;
  expiresAt?: string;
  actionUrl?: string;
  actionText?: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationChannel {
  type: 'email' | 'sms' | 'push' | 'in-app' | 'webhook';
  status: 'pending' | 'sent' | 'delivered' | 'failed';
  sentAt?: string;
  deliveredAt?: string;
  error?: string;
  metadata?: any;
}

export interface NotificationTemplate {
  _id: string;
  name: string;
  displayName: string;
  description: string;
  category: string;
  type: Notification['type'];
  subject: string;
  bodyTemplate: string;
  htmlTemplate?: string;
  variables: TemplateVariable[];
  defaultChannels: NotificationChannel['type'][];
  isActive: boolean;
  createdBy: {
    _id: string;
    name: string;
  };
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateVariable {
  name: string;
  displayName: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'object';
  required: boolean;
  defaultValue?: any;
  description: string;
}

export interface NotificationPreferences {
  _id: string;
  userId: string;
  emailNotifications: {
    enabled: boolean;
    categories: string[];
    frequency: 'immediate' | 'hourly' | 'daily' | 'weekly';
    quietHours: {
      enabled: boolean;
      start: string; // HH:mm
      end: string; // HH:mm
    };
  };
  smsNotifications: {
    enabled: boolean;
    categories: string[];
    urgentOnly: boolean;
  };
  pushNotifications: {
    enabled: boolean;
    categories: string[];
    showPreview: boolean;
  };
  inAppNotifications: {
    enabled: boolean;
    categories: string[];
    autoMarkAsRead: boolean;
    retentionDays: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface NotificationRule {
  _id: string;
  name: string;
  description: string;
  trigger: {
    event: string; // e.g., 'project.created', 'issue.assigned'
    conditions: RuleCondition[];
  };
  actions: RuleAction[];
  isActive: boolean;
  priority: number;
  createdBy: {
    _id: string;
    name: string;
  };
  executionCount: number;
  lastExecuted?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RuleCondition {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than' | 'in' | 'not_in';
  value: any;
}

export interface RuleAction {
  type: 'send_notification' | 'create_task' | 'send_email' | 'webhook';
  config: any;
}

export interface NotificationFilters {
  type?: string;
  category?: string;
  priority?: string;
  status?: string;
  isRead?: boolean;
  dateFrom?: string;
  dateTo?: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
}

export interface NotificationStats {
  totalNotifications: number;
  unreadNotifications: number;
  notificationsByType: {
    type: string;
    count: number;
  }[];
  notificationsByCategory: {
    category: string;
    count: number;
  }[];
  deliveryStats: {
    channel: string;
    sent: number;
    delivered: number;
    failed: number;
    deliveryRate: number;
  }[];
  recentActivity: {
    date: string;
    count: number;
  }[];
}

// Axios instance with auth token
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Notifications Service
class NotificationsService {
  // Notification Management
  async getNotifications(filters?: NotificationFilters): Promise<Notification[]> {
    const response = await api.get('/notifications', { params: filters });
    // Backend returns an object: { notifications: [], page, total, unreadCount }
    const data = response.data;
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.notifications) ? data.notifications : [];
  }

  async getNotification(id: string): Promise<Notification> {
    const response = await api.get(`/notifications/${id}`);
    return response.data;
  }

  async createNotification(notification: Omit<Notification, '_id' | 'createdAt' | 'updatedAt' | 'status' | 'isRead'>): Promise<Notification> {
    const response = await api.post('/notifications', notification);
    return response.data;
  }

  async updateNotification(id: string, notification: Partial<Notification>): Promise<Notification> {
    const response = await api.put(`/notifications/${id}`, notification);
    return response.data;
  }

  async deleteNotification(id: string): Promise<void> {
    await api.delete(`/notifications/${id}`);
  }

  // Notification Status Management
  async markAsRead(id: string): Promise<Notification> {
    const response = await api.patch(`/notifications/${id}/read`);
    return response.data;
  }

  async markAsUnread(id: string): Promise<Notification> {
    const response = await api.patch(`/notifications/${id}/unread`);
    return response.data;
  }

  async markAllAsRead(filters?: NotificationFilters): Promise<void> {
    await api.patch('/notifications/mark-all-read', { filters });
  }

  // Bulk Operations
  async bulkMarkAsRead(notificationIds: string[]): Promise<void> {
    await api.patch('/notifications/bulk-read', { notificationIds });
  }

  async bulkDelete(notificationIds: string[]): Promise<void> {
    await api.delete('/notifications/bulk-delete', { data: { notificationIds } });
  }

  // Template Management
  async getTemplates(): Promise<NotificationTemplate[]> {
    const response = await api.get('/notification-templates');
    const data = response.data;
    // Support both array and object-wrapped responses
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.templates) ? data.templates : [];
  }

  async getTemplate(id: string): Promise<NotificationTemplate> {
    const response = await api.get(`/notification-templates/${id}`);
    return response.data;
  }

  async createTemplate(template: Omit<NotificationTemplate, '_id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'usageCount'>): Promise<NotificationTemplate> {
    const response = await api.post('/notification-templates', template);
    return response.data;
  }

  async updateTemplate(id: string, template: Partial<NotificationTemplate>): Promise<NotificationTemplate> {
    const response = await api.put(`/notification-templates/${id}`, template);
    return response.data;
  }

  async deleteTemplate(id: string): Promise<void> {
    await api.delete(`/notification-templates/${id}`);
  }

  async sendFromTemplate(templateId: string, recipients: string[], variables: any): Promise<Notification[]> {
    const response = await api.post(`/notification-templates/${templateId}/send`, {
      recipients,
      variables,
    });
    return response.data;
  }

  // Preferences Management
  async getPreferences(userId?: string): Promise<NotificationPreferences> {
    const params = userId ? { userId } : {};
    const response = await api.get('/notification-preferences', { params });
    return response.data;
  }

  async updatePreferences(preferences: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
    const response = await api.put('/notification-preferences', preferences);
    return response.data;
  }

  // Rules Management
  async getRules(): Promise<NotificationRule[]> {
    const response = await api.get('/notification-rules');
    return response.data;
  }

  async getRule(id: string): Promise<NotificationRule> {
    const response = await api.get(`/notification-rules/${id}`);
    return response.data;
  }

  async createRule(rule: Omit<NotificationRule, '_id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'executionCount'>): Promise<NotificationRule> {
    const response = await api.post('/notification-rules', rule);
    return response.data;
  }

  async updateRule(id: string, rule: Partial<NotificationRule>): Promise<NotificationRule> {
    const response = await api.put(`/notification-rules/${id}`, rule);
    return response.data;
  }

  async deleteRule(id: string): Promise<void> {
    await api.delete(`/notification-rules/${id}`);
  }

  async testRule(id: string, testData: any): Promise<any> {
    const response = await api.post(`/notification-rules/${id}/test`, testData);
    return response.data;
  }

  // Real-time Notifications
  async getUnreadCount(): Promise<number> {
    const response = await api.get('/notifications/unread-count');
    return response.data.count;
  }

  async getRecentNotifications(limit: number = 10): Promise<Notification[]> {
    const response = await api.get('/notifications/recent', { params: { limit } });
    return response.data;
  }

  // Statistics and Reports
  async getStats(dateFrom?: string, dateTo?: string): Promise<NotificationStats> {
    const params: any = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    
    const response = await api.get('/notifications/stats', { params });
    return response.data;
  }

  async getDeliveryReport(dateFrom?: string, dateTo?: string): Promise<any> {
    const params: any = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    
    const response = await api.get('/notifications/delivery-report', { params });
    return response.data;
  }

  // Push Notification Management
  async registerPushToken(token: string, deviceType: 'ios' | 'android' | 'web'): Promise<void> {
    await api.post('/notifications/push-token', { token, deviceType });
  }

  async unregisterPushToken(token: string): Promise<void> {
    await api.delete('/notifications/push-token', { data: { token } });
  }

  // Export
  async exportNotifications(filters?: NotificationFilters, format: 'csv' | 'excel' | 'json' = 'csv'): Promise<Blob> {
    const response = await api.get('/notifications/export', {
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  }

  // Cleanup
  async cleanupOldNotifications(olderThanDays: number): Promise<{ deletedCount: number }> {
    const response = await api.delete('/notifications/cleanup', {
      data: { olderThanDays },
    });
    return response.data;
  }
}

export const notificationsService = new NotificationsService();
export default notificationsService;